import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import SalesCharts from "../components/ui/SalesCharts";
import SalesByDueDateCharts from "../components/ui/SalesByDueDateCharts";
import {
  Users,
  Server,
  DollarSign,
  Ticket,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import { API } from "../App";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [salesData, setSalesData] = useState({
    daily: [],
    weekly: [],
    monthly: [],
    yearly: [],
  });

  useEffect(() => {
    fetchStats();
    fetchSales();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/dashboard/sales`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSalesData(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const formatCurrency = (value) =>
    value?.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </AdminLayout>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color, gradient, testId }) => (
    <div
      className={`bg-gradient-to-br ${gradient} rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
      data-testid={testId}
    >
      <div className="flex items-center mb-4 ">
        <div
          className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center shadow-lg mr-3`}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-3xl font-bold text-slate-900">{value}</p>
          <p className="text-sm font-medium text-slate-600">{label}</p>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Dashboard</h1>
          <p className="text-slate-600">
            Overview of your ISP management system
          </p>
        </div>
        {/* {JSON.stringify(stats)} */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <StatCard
            icon={Users}
            label="New Clients"
            value={stats?.new_clients || 0}
            color="bg-emerald-500"
            gradient="from-emerald-50 to-emerald-100"
            testId="stat-new-clients"
          />
          <StatCard
            icon={Users}
            label="Paid Clients"
            value={stats?.paid_clients || 0}
            color="bg-emerald-700"
            gradient="from-emerald-50 to-emerald-100"
            testId="stat-paid-clients"
          />
          <StatCard
            icon={Users}
            label="Unpaid Clients"
            value={stats?.unpaid_clients || 0}
            color="bg-red-500"
            gradient="from-red-50 to-red-100"
            testId="stat-unpaid-clients"
          />
          <StatCard
            icon={Users}
            label="Disconnected Clients"
            value={stats?.disconnected_clients || 0}
            color="bg-slate-500"
            gradient="from-slate-50 to-slate-100"
            testId="stat-disconnected-clients"
          />
          <StatCard
            icon={Users}
            label="Extended Clients"
            value={stats?.extended_clients || 0}
            color="bg-blue-500"
            gradient="from-blue-50 to-blue-100"
            testId="stat-extended-clients"
          />
          {/* <StatCard
            icon={AlertCircle}
            label="Upcoming Due (7 days)"
            value={stats?.upcoming_due || 0}
            color="bg-orange-500"
            gradient="from-orange-50 to-orange-100"
            testId="stat-upcoming-due"
          /> */}

          <StatCard
            icon={Users}
            label="Total Clients"
            value={stats?.total_clients || 0}
            color="bg-blue-500"
            gradient="from-blue-50 to-blue-100"
            testId="stat-total-clients"
          />
          <StatCard
            icon={TrendingUp}
            label="Active Clients"
            value={stats?.active_clients || 0}
            color="bg-emerald-500"
            gradient="from-emerald-50 to-emerald-100"
            testId="stat-active-clients"
          />
          <StatCard
            icon={TrendingUp}
            label="Inactive Clients"
            value={stats?.inactive_clients || 0}
            color="bg-amber-500"
            gradient="from-amber-50 to-amber-100"
            testId="stat-inactive-clients"
          />

          <StatCard
            icon={DollarSign}
            label="Expected Monthly Revenue"
            value={`₱${formatCurrency(stats?.monthly_revenue) || "0.00"}`}
            color="bg-indigo-500"
            gradient="from-indigo-50 to-indigo-100"
            testId="stat-monthly-revenue"
          />

          <StatCard
            icon={Ticket}
            label="Open Tickets"
            value={stats?.open_tickets || 0}
            color="bg-amber-500"
            gradient="from-amber-50 to-amber-100"
            testId="stat-open-tickets"
          />
        </div>
        {salesData && <SalesByDueDateCharts data={salesData} />}

        {/* <pre>{JSON.stringify(salesData, null, 2)}</pre> */}

        <div className="grid grid-cols-1 gap-4 mb-8 mt-4 xl:grid-cols-2">
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-500 p-4 rounded-xl border flex justify-between items-center">
            <div>
              <p className="text-md text-blue-800 font-bold">
                Projected Sales from 1 to 15
              </p>
              <p className="text-lg font-bold">
                ₱{salesData?.total_projected_1_15_formatted}
              </p>
            </div>
            <DollarSign className="w-10 h-10 text-slate-300 mt-1" />
          </div>
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-500 p-4 rounded-xl border flex justify-between items-center">
            <div>
              <p className="text-md text-blue-800 font-bold">
                Projected Sales from 16 to 30
              </p>
              <p className="text-lg font-bold">
                ₱{salesData?.total_projected_16_30_formatted}
              </p>
            </div>
            <DollarSign className="w-10 h-10 text-slate-300 mt-1" />
          </div>
        </div>

        <div className="my-6">
          <h1 className="text-2xl font-bold text-slate-900">Current Sales</h1>
          <p className="text-slate-500 text-sm">
            Track projected revenue expected to close by the selected due date.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-6 mt-6 xl:grid-cols-2">
          <div className="bg-white p-4 rounded-xl border flex justify-between items-center">
            <div>
              <p className="text-xs text-gray-500">Sales for the Today</p>
              <p className="text-lg font-bold">₱{salesData?.total_daily}</p>
            </div>
            <DollarSign className="w-10 h-10 text-slate-300 mt-1" />
          </div>
          <div className="bg-white p-4 rounded-xl border flex justify-between items-center">
            <div>
              <p className="text-xs text-gray-500">Sales for this Week</p>
              <p className="text-lg font-bold">₱{salesData?.total_weekly}</p>
            </div>
            <DollarSign className="w-10 h-10 text-slate-300 mt-1" />
          </div>
          <div className="bg-white p-4 rounded-xl border flex justify-between items-center">
            <div>
              <p className="text-xs text-gray-500">Sales for this Month</p>
              <p className="text-lg font-bold">₱{salesData?.total_monthly}</p>
            </div>
            <DollarSign className="w-10 h-10 text-slate-300 mt-1" />
          </div>
          <div className="bg-white p-4 rounded-xl border flex justify-between items-center">
            <div>
              <p className="text-xs text-gray-500">Sales for this Year</p>
              <p className="text-lg font-bold">₱{salesData?.total_yearly}</p>
            </div>
            <DollarSign className="w-10 h-10 text-slate-300 mt-1" />
          </div>
        </div>
        {salesData && <SalesCharts data={salesData} />}
      </div>
    </AdminLayout>
  );
}
